import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, fetchMe, ApiError } from '../api/client';
import { useAuthStore } from '../auth/store';
import './LoginPage.css';

export function LoginPage(): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const setMe = useAuthStore((s) => s.setMe);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ email, password });
      const { user, ...rest } = await fetchMe();
      // Post-login /api/me must include a user — if it doesn't, the
      // server is in a weird state; treat it as a failed login.
      if (!user) throw new ApiError(401, null, 'Invalid email or password');
      setMe({ user, ...rest });
      navigate('/app', { replace: true });
    } catch (err) {
      // Generic error — never leak whether the email exists
      setError(err instanceof ApiError && err.status === 401
        ? 'Invalid email or password'
        : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <form className="login-form" onSubmit={onSubmit} noValidate>
        <h1>Street Forensic Services</h1>
        <p className="login-subtitle">Sign in to continue</p>

        <label>
          <span>Email</span>
          <input
            type="email"
            className="form-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            autoFocus
          />
        </label>

        <label>
          <span>Password</span>
          <input
            type="password"
            className="form-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
        </label>

        {error && <p className="login-error" role="alert">{error}</p>}

        <button type="submit" disabled={submitting} className="btn-cyan-primary">
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
